import { clsx, type ClassValue } from 'clsx';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatDate(date: string | Date, formatStr = 'MMM d, yyyy'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr);
}

export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

export function getEngagementScoreColor(score: number): string {
  if (score >= 76) return 'text-emerald-400';
  if (score >= 51) return 'text-amber-400';
  if (score >= 26) return 'text-orange-400';
  return 'text-red-400';
}

export function getEngagementScoreLabel(score: number): string {
  if (score >= 91) return 'Excellent';
  if (score >= 76) return 'High';
  if (score >= 51) return 'Medium';
  if (score >= 26) return 'Low';
  return 'Critical';
}

export function getPipelineStageColor(stage: string): string {
  switch (stage) {
    case 'New':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'Engaged':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    case 'Stalled':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    case 'Dropped':
      return 'bg-red-500/20 text-red-300 border-red-500/30';
    case 'Converted':
      return 'bg-teal-500/20 text-teal-300 border-teal-500/30';
    default:
      return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  }
}

export function getChannelIcon(channel: string): string {
  switch (channel) {
    case 'email':
      return '📧';
    case 'sms':
      return '📱';
    case 'voice':
      return '📞';
    case 'chat':
      return '💬';
    default:
      return '📬';
  }
}

export function getSequenceTypeIcon(type: string): string {
  switch (type) {
    case 'NEW_LEAD':
      return '🟢';
    case 'STALLED_APP':
      return '🟡';
    case 'DROPPED_LEAD':
      return '🔵';
    case 'PAST_CLIENT_REFI':
      return '🟠';
    default:
      return '⚪';
  }
}

export function getTrendIcon(change: number): string {
  if (change > 0) return '↗️';
  if (change < 0) return '↘️';
  return '→';
}

export function getTrendColor(change: number): string {
  if (change > 0) return 'text-emerald-400';
  if (change < 0) return 'text-red-400';
  return 'text-slate-400';
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}